// src/pages/student/exams/StudentResults.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Collapse,
  Form,
  InputGroup,
  ListGroup,
  Modal,
  ProgressBar,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { getAuth } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  where,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../../services/firebase";

dayjs.extend(duration);

/** Save analysis to attempts/{uid} with merge */
const saveAnalysisToFirestore = async ({ schoolId, examId, uid, payload }) => {
  try {
    const attRef = doc(db, "schools", schoolId, "exams", examId, "attempts", uid);
    await setDoc(
      attRef,
      {
        // top-level mirrors for easy querying
        evaluatedScore: payload.evaluatedScore,
        totalMarks: payload.totalMarks,
        answeredCount: payload.answeredCount,
        correctCount: payload.correctCount,
        percent: payload.percent,
        accuracy: payload.accuracy,
        resultsByQuestion: payload.resultsByQuestion,

        // nested detailed analysis
        analysis: payload,

        // audit
        lastAnalysisAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (e) {
    console.warn("saveAnalysisToFirestore failed:", e?.message || e);
  }
};

/* ---------------- helpers ---------------- */
const toJS = (t) => (t?.toDate ? t.toDate() : t ? new Date(t) : null);
const norm = (s) => String(s || "").trim().toLowerCase();

/** Try to read numeric score fields with fallbacks */
const readScore = (obj = {}) => {
  const v =
    obj.score ??
    obj.marksObtained ??
    obj.evaluatedScore ??
    obj.obtained ??
    obj.points ??
    0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const readTotal = (obj = {}) => {
  const v =
    obj.totalMarks ?? obj.maxScore ?? obj.fullMarks ?? obj.total ?? obj.outOf ?? 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Normalize numeric/letter to "A", "B", "C", ...
const toLetterKey = (v) => {
  const n = Number(v);
  if (Number.isFinite(n) && String(v) !== "") return String.fromCharCode(65 + Math.max(0, Math.floor(n)));
  return String(v ?? "").trim().toUpperCase();
};

// Prefer sq.correctKey; else derive from options[].isCorrect
const getCaseCorrectKey = (sq = {}) => {
  const viaKey = String(sq.correctKey || "").trim().toUpperCase();
  if (viaKey) return viaKey;
  const opt = (sq.options || []).find((o) => o?.isCorrect);
  return opt?.key ? String(opt.key).toUpperCase() : "";
};

// Stable fallback id for sub if sq.id missing
const subIdFor = (parentQ, sq, idx) => sq?.id || `${parentQ.id}__sub_${idx}`;

// normalize student answer value (supports primitives or {value})
const getAnsValue = (ans) =>
  ans && typeof ans === "object" && "value" in ans ? ans.value : ans;

// Read student's choice for a case sub, supporting both shapes:
//  - answers[sq.id] = "A" | 1
//  - answers[parentQ.id] = { value: { [subIndex]: "A" | 1 } }
const readCaseChoice = (answers = {}, parentQ, subIndex, sq) => {
  const bySubId = answers[subIdFor(parentQ, sq, subIndex)];
  if (bySubId != null) return getAnsValue(bySubId);
  const parent = answers[parentQ?.id];
  if (parent && typeof parent === "object" && parent.value && typeof parent.value === "object") {
    return parent.value[subIndex];
  }
  return null;
};

// --- For Assertion–Reason rendering in UI ---
const buildARStemHtml = (q) => {
  const head = q.questionHtml ? `<div>${q.questionHtml}</div>` : "";
  const a = q.assertionHtml || "";
  const r = q.reasonHtml || "";
  return `
    ${head}
    <div><strong>Assertion (A):</strong> ${a || "<em>—</em>"}</div>
    <div><strong>Reason (R):</strong> ${r || "<em>—</em>"}</div>
  `;
};

// marks helpers
const getMarks = (q) => Number(q.marks ?? q.maxMarks ?? 1) || 1;
const getNegMarks = (q) =>
  Math.max(0, Number(q.negativeMarks ?? q.negative ?? q.neg ?? 0) || 0);

// get correct shape based on questionType (non case-based)
const getCorrectShape = (q) => {
  const qt = norm(q.questionType);

  if (qt === "mcq" || qt === "mcq_descriptive") {
    const opt = (q.options || []).find((o) => o?.isCorrect);
    return { type: "key", value: opt?.key ?? null }; // "A"…"D"
  }

  if (qt === "multi_correct") {
    const keys = (q.options || [])
      .filter((o) => o?.isCorrect)
      .map((o) => String(o.key));
    return { type: "keyset", value: keys };
  }

  if (qt === "true_false" || qt === "tf") {
    return { type: "scalar", value: String(q.trueFalseAnswer || "True") };
  }

  if (qt === "match") {
    if (Number.isInteger(q.correctOptionIndex)) {
      return { type: "index", value: Number(q.correctOptionIndex) }; // 0..3
    }
    return { type: "unknown", value: null };
  }

  if (qt === "assertion_reason" || qt === "ar") {
    return { type: "scalar", value: String(q.arAnswer || "A") };
  }

  // descriptive / statement_based / case_based handled elsewhere
  return { type: "unknown", value: null };
};

// compare student value vs correct shape
const isAnswerCorrect = (q, studentValue, correctShape) => {
  const qt = norm(q.questionType);
  const { type, value } = correctShape;
  if (type === "unknown") return null;

  const toKey = (v) => String(v ?? "").trim().toUpperCase();
  const toKeyArray = (v) =>
    Array.isArray(v)
      ? v.map((x) => toKey(x))
      : String(v ?? "")
          .split(",")
          .map((x) => toKey(x))
          .filter(Boolean);

  if (qt === "mcq" || qt === "mcq_descriptive") {
    if (type !== "key") return null;
    const sv = studentValue;
    const sKey =
      Number.isFinite(Number(sv)) && String(sv) !== ""
        ? String.fromCharCode(65 + Number(sv))
        : toKey(sv);
    return sKey && value ? sKey === toKey(value) : null;
  }

  if (qt === "multi_correct") {
    if (type !== "keyset") return null;
    const sKeys = new Set(toKeyArray(studentValue));
    const cKeys = new Set(value.map((x) => toKey(x)));
    if (sKeys.size !== cKeys.size) return false;
    for (const k of sKeys) if (!cKeys.has(k)) return false;
    return true;
  }

  if (qt === "true_false" || qt === "tf") {
    if (type !== "scalar") return null;
    return String(studentValue) === String(value);
  }

  if (qt === "match") {
    if (type !== "index") return null;
    return Number(studentValue) === Number(value); // option index 0..3
  }

  if (qt === "assertion_reason" || qt === "ar") {
    if (type !== "scalar") return null;
    const toLetter = (v) => {
      const n = Number(v);
      if (Number.isFinite(n)) return String.fromCharCode(65 + Math.max(0, Math.floor(n)));
      return toKey(v);
    };
    const sKey = toLetter(studentValue);
    const cKey = toLetter(value);
    return sKey && cKey ? sKey === cKey : null;
  }

  return null; // not auto-graded
};

// full local evaluation (fills resultsByQuestion, score, totals, negative marking)
const evaluateAttemptLocally = (attempt, questions) => {
  if (!attempt || !questions?.length) {
    return {
      resultsByQuestion: {},
      answeredCount: Number(attempt?.answeredCount ?? 0),
      correctCount: Number(attempt?.correctCount ?? 0),
      score: readScore(attempt),
      total: readTotal(attempt),
    };
  }

  const answers = attempt.answers || {};
  const resultsByQuestion = {};
  let total = 0;
  let score = 0;
  let answered = 0;
  let correct = 0;

  for (const q of questions) {
    const qt = norm(q.questionType);

    // ----- CASE-BASED -----
    if (qt === "case_based" && Array.isArray(q.caseSubQs) && q.caseSubQs.length) {
      for (const [idx, sq] of q.caseSubQs.entries()) {
        const subId = subIdFor(q, sq, idx);
        const subMarks = Number(sq.marks ?? q.marks ?? 1) || 1;
        const subNeg = Number(sq.negativeMarks ?? q.negativeMarks ?? 0) || 0;
        total += subMarks;

        const raw = readCaseChoice(answers, q, idx, sq);
        const chosenKey = toLetterKey(getAnsValue(raw));
        const isAnswered = chosenKey !== "";
        if (isAnswered) answered += 1;

        const correctKey = getCaseCorrectKey(sq);
        const ok = chosenKey && correctKey ? chosenKey === correctKey : null;

        if (ok === true) {
          score += subMarks;
          correct += 1;
          resultsByQuestion[subId] = {
            correct: true,
            marks: subMarks,
            evaluated: true,
            parentId: q.id,
          };
        } else if (ok === false) {
          score -= subNeg;
          resultsByQuestion[subId] = {
            correct: false,
            marks: -subNeg,
            evaluated: true,
            parentId: q.id,
          };
        } else {
          resultsByQuestion[subId] = {
            correct: false,
            marks: 0,
            evaluated: false,
            parentId: q.id,
          };
        }
      }

    // ----- NORMAL QUESTIONS -----
    } else {
      const marks = getMarks(q);
      const neg = getNegMarks(q);
      total += marks;

      const raw = answers[q.id];
      const ansVal = getAnsValue(raw);
      const isAnswered =
        Array.isArray(ansVal) ? ansVal.length > 0 : String(ansVal ?? "").trim() !== "";
      if (isAnswered) answered += 1;

      const correctShape = getCorrectShape(q);
      const ok = isAnswerCorrect(q, ansVal, correctShape);

      if (ok === null) {
        // not auto-gradable (descriptive, statement_based, etc.)
        resultsByQuestion[q.id] = { correct: false, marks: 0, evaluated: false };
      } else if (ok) {
        score += marks;
        correct += 1;
        resultsByQuestion[q.id] = { correct: true, marks, evaluated: true };
      } else {
        score -= neg;
        resultsByQuestion[q.id] = { correct: false, marks: -neg, evaluated: true };
      }
    }
  }

  return {
    resultsByQuestion,
    answeredCount: answered,
    correctCount: correct,
    score,
    total,
  };
};

/** Build compact analysis payload to store in Firestore */
const buildAnalysisPayload = ({
  exam,
  attempt,
  scoreInfo,
  breakdown,
  ranking,
  examTotalMarks,
}) => {
  const percent = Number.isFinite(scoreInfo.percent) ? Math.round(scoreInfo.percent) : 0;
  const accuracy = Number.isFinite(scoreInfo.accuracy) ? Math.round(scoreInfo.accuracy) : 0;

  return {
    examId: exam?.id || null,
    examName: exam?.name || null,

    // normalized core scores
    evaluatedScore: Number(scoreInfo.score || 0),
    totalMarks: Number(scoreInfo.total || examTotalMarks || 0),
    percent,
    accuracy,
    answeredCount: Number(attempt?.answeredCount || 0),
    correctCount: Number(attempt?.correctCount || 0),

    // basic timing
    startedAt: attempt?.startedAt || null,
    submittedAt: attempt?.submittedAt || null,
    durationSec: Number(scoreInfo.timeSec || 0),

    // breakdowns (compact, sortable)
    subjectBreakdown: (breakdown?.subject || []).map((r) => ({
      subject: r.key,
      questions: r.totalQ,
      correct: r.correctQ,
      acc: r.acc,
      marks: { awarded: r.sumAward, total: r.sumMarks },
    })),
    topicBreakdown: (breakdown?.topic || []).map((r) => ({
      topic: r.key,
      questions: r.totalQ,
      correct: r.correctQ,
      acc: r.acc,
      marks: { awarded: r.sumAward, total: r.sumMarks },
    })),

    // rank signals (optional; recomputed each load)
    rank: ranking?.rank ?? null,
    percentile: ranking?.percentile ?? null,

    // minimal per-question results map (id -> {marks, correct, evaluated})
    resultsByQuestion: attempt?.resultsByQuestion || {},

    // bookkeeping
    analysisVersion: 1,
    analysisUpdatedAt: serverTimestamp(),
  };
};

/* ---------------- Page ---------------- */
export default function StudentResults() {
  const { examId } = useParams();
  const nav = useNavigate();
  const auth = getAuth();
  const uid = auth.currentUser?.uid || null;

  // session basics
  const [schoolId] = useState(() => localStorage.getItem("schoolId"));
  const [studentId] = useState(() => localStorage.getItem("studentId"));
  const [studentName] = useState(() => localStorage.getItem("studentName") || "");

  // data
  const [exam, setExam] = useState(null);
  const [attempt, setAttempt] = useState(null); // merged with local evaluation
  const [allAttempts, setAllAttempts] = useState([]);
  const [questions, setQuestions] = useState([]);

  // filters
  const [fSubject, setFSubject] = useState("all");
  const [fTopic, setFTopic] = useState("all");
  const [fType, setFType] = useState("all");
  const [fCorrect, setFCorrect] = useState("all"); // all | correct | wrong | unattempted
  const [q, setQ] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);

  // map modal
  const [showMap, setShowMap] = useState(false);
  
  const [pendingScrollId, setPendingScrollId] = useState(null);

  // loading
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // ------------ LOAD DATA ------------
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setErr("");
        if (!schoolId || !examId || !uid) {
          setErr("Missing context. Please sign in again.");
          setLoading(false);
          return;
        }

        // exam
        const examRef = doc(db, "schools", schoolId, "exams", examId);
        const examSnap = await getDoc(examRef);
        if (!examSnap.exists()) {
          setErr("Exam not found.");
          setLoading(false);
          return;
        }
        const e = { id: examSnap.id, ...examSnap.data() };
        setExam(e);

        // this student attempt
        const attRef = doc(db, "schools", schoolId, "exams", examId, "attempts", uid);
        const attSnap = await getDoc(attRef);
        const rawAttempt = attSnap.exists() ? { id: attSnap.id, ...attSnap.data() } : null;

        // questions for this exam (via templateId)
        let qs = [];
        const templateId = e.templateId;
        if (templateId) {
          const qCol = collection(db, "schools", schoolId, "questions");
          const qy = query(qCol, where("templateId", "==", templateId), limit(800));
          const qSnap = await getDocs(qy);
          qs = qSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        }
        setQuestions(qs);

        // leaderboard attempts
        const aCol = collection(db, "schools", schoolId, "exams", examId, "attempts");
        const aSnap = await getDocs(aCol);
        const atts = aSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setAllAttempts(atts);

        // merge local evaluation so UI always has marks/correctness
        if (rawAttempt) {
          const evald = evaluateAttemptLocally(rawAttempt, qs);
          const merged = {
            ...rawAttempt,
            resultsByQuestion: evald.resultsByQuestion,
            answeredCount: evald.answeredCount,
            correctCount: evald.correctCount,
            score: evald.score,
            totalMarks: evald.total,
            evaluatedScore: evald.score,
          };
          setAttempt(merged);
        } else {
          setAttempt(null);
        }
      } catch (e) {
        console.error(e);
        setErr(e?.message || "Failed to load results.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [schoolId, examId, uid]);

  // ------------ DERIVED METRICS (ORDER MATTERS) ------------
  const scoreInfo = useMemo(() => {
    if (!attempt) {
      return {
        score: 0,
        total: 0,
        percent: 0,
        answered: 0,
        correct: 0,
        accuracy: 0,
        timeSec: 0,
      };
    }
    const score =
      Number.isFinite(attempt.score)
        ? Number(attempt.score)
        : Number(attempt.evaluatedScore || 0);
    let total = Number(attempt.totalMarks || readTotal(attempt));

    if (!total && questions.length) {
      total = questions.reduce((sum, q) => {
        const qt = String(q.questionType || "").toLowerCase();
        if (qt === "case_based" && Array.isArray(q.caseSubQs) && q.caseSubQs.length) {
          return (
            sum +
            q.caseSubQs.reduce(
              (s, sq) => s + (Number(sq.marks ?? q.marks ?? 1) || 1),
              0
            )
          );
        }
        return sum + getMarks(q);
      }, 0);
    }

    const answered = Number(attempt.answeredCount ?? 0);
    const correct = Number(attempt.correctCount ?? 0);

    const percent = total ? Math.max(0, Math.min(100, (score / total) * 100)) : 0;
    const accuracy = answered ? Math.max(0, Math.min(100, (correct / answered) * 100)) : 0;

    const start = toJS(attempt.startedAt);
    const end = toJS(attempt.submittedAt);
    const timeSec =
      start && end ? Math.max(0, Math.floor((end.getTime() - start.getTime()) / 1000)) : 0;

    return { score, total, percent, answered, correct, accuracy, timeSec };
  }, [attempt, questions]);

  const examTotalMarks = useMemo(() => {
    if (!questions.length) return 0;
    let total = 0;
    for (const q of questions) {
      const qt = String(q.questionType || "").toLowerCase();
      if (qt === "case_based" && Array.isArray(q.caseSubQs) && q.caseSubQs.length) {
        for (const sq of q.caseSubQs) {
          total += Number(sq.marks ?? q.marks ?? 1) || 1;
        }
      } else {
        total += Number(q.marks ?? 1) || 1;
      }
    }
    return total;
  }, [questions]);

  const ranking = useMemo(() => {
    if (!allAttempts.length) {
      return { rank: null, percentile: null, top: [] };
    }

    const rows = allAttempts.map((a) => {
      const score =
        Number(a.score ?? a.evaluatedScore ?? a.marksObtained ?? a.obtained ?? 0) || 0;

      // try attempt total; if missing, fall back to computed exam total
      const totalRaw =
        Number(a.totalMarks ?? a.maxScore ?? a.fullMarks ?? a.total ?? a.outOf ?? 0);
      const total = totalRaw || examTotalMarks || 0;

      // if both are missing, percent may be saved in doc already
      const percent =
        total > 0
          ? (score / total) * 100
          : Number.isFinite(a.percent) ? Number(a.percent) : 0;

      return {
        id: a.id,
        name: a.byStudent?.name || a.studentName || a.uid || "Student",
        score,
        total: total || null,
        percent,
        submittedAt: toJS(a.submittedAt),
      };
    });

    rows.sort((x, y) => {
      if (y.percent !== x.percent) return y.percent - x.percent;
      const tx = x.submittedAt?.getTime() || Infinity;
      const ty = y.submittedAt?.getTime() || Infinity;
      return tx - ty;
    });

    const myIdx = rows.findIndex((r) => r.id === uid);
    const rank = myIdx >= 0 ? myIdx + 1 : null;
    const N = rows.length;
    const myPercent = scoreInfo.percent || 0;
    const below = rows.filter((r) => r.percent < myPercent).length;
    const percentile = N ? Math.round((below / N) * 100) : null;

    const top = rows.slice(0, 10);
    return { rank, percentile, top };
  }, [allAttempts, uid, scoreInfo.percent, examTotalMarks]);

  const qItems = useMemo(() => {
    if (!attempt || !questions.length) return [];
    const rbq =
      attempt.resultsByQuestion && typeof attempt.resultsByQuestion === "object"
        ? attempt.resultsByQuestion
        : {};

    const answers = attempt.answers || {};
    const items = [];

    const typeLabel = (qt) => {
      const t = norm(qt);
      if (t === "mcq") return "MCQ";
      if (t === "multi_correct") return "Multi Correct";
      if (t === "true_false") return "True/False";
      if (t === "match") return "Match";
      if (t === "assertion_reason" || t === "ar") return "A/R";
      if (t === "case_based") return "Case";
      if (t === "mcq_descriptive") return "MCQ+Desc";
      if (t === "descriptive") return "Descriptive";
      if (t === "statement_based") return "Statement";
      return (qt || "").toUpperCase();
    };

    const toKey = (v) =>
      Number.isFinite(Number(v)) && String(v) !== ""
        ? String.fromCharCode(65 + Number(v))
        : String(v ?? "").toUpperCase();

    // quick map for parent explanations
    const parentExpMap = new Map(questions.map((q) => [q.id, q.explanationHtml || ""]));

    questions.forEach((q) => {
      const qt = norm(q.questionType);

      if (qt === "case_based" && Array.isArray(q.caseSubQs) && q.caseSubQs.length) {
        q.caseSubQs.forEach((sq, idx) => {
          const qid = subIdFor(q, sq, idx);
          const evalInfo = rbq[qid] || {};
          const studentRaw = readCaseChoice(answers, q, idx, sq);
          const studentVal = getAnsValue(studentRaw);
          const chosenKey = toKey(studentVal);
          const correctKey = getCaseCorrectKey(sq);
          const awarded = Number(evalInfo?.marks ?? 0) || 0;
          const marks = Number(sq.marks ?? q.marks ?? 1) || 1;
          const correctness = evalInfo.evaluated
            ? (evalInfo.correct ? "correct" : "wrong")
            : chosenKey
            ? "wrong"
            : "unattempted";

          items.push({
            id: qid,
            parentId: q.id,
            indexLabel: `Case ${idx + 1}`,
            subject: q.subject || "General",
            topic: q.topic || "—",
            type: "MCQ",
            typeLabel: "Case",
            questionHtml: sq.questionHtml || "",
              passageHtml: q.casePassageHtml || q.passageHtml || "",   // ⬅️ add this

            explanationHtml: parentExpMap.get(q.id) || "",
            options: sq.options || [],
            chosen: chosenKey || "",
            correct: correctKey || "",
            awarded,
            marks,
            correctness,
            sectionKey: q.sectionKey || "",
          });
        });
        return;
      }

      const qid = q.id;
      const evalInfo = rbq[qid] || {};
      const studentRaw = answers[qid];
      const studentVal = getAnsValue(studentRaw);
      const answered = String(studentVal ?? "").trim() !== "";
      const correctness = evalInfo.evaluated
        ? evalInfo.correct
          ? "correct"
          : "wrong"
        : answered
        ? "wrong"
        : "unattempted";

      let chosenLabel = "";
      let correctLabel = "";
      const tLabel = typeLabel(q.questionType);
      const stemHtml =
        qt === "assertion_reason" || qt === "ar"
          ? buildARStemHtml(q)
          : (q.questionHtml || "");

      if (qt === "mcq" || qt === "mcq_descriptive") {
        const chosen = toKey(studentVal);
        const correct = (q.options || []).find((o) => o.isCorrect)?.key ?? "";
        chosenLabel = chosen;
        correctLabel = correct;
      } else if (qt === "multi_correct") {
        const chosen = Array.isArray(studentVal)
          ? studentVal
          : String(studentVal || "")
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
        const correct = (q.options || [])
          .filter((o) => o.isCorrect)
          .map((o) => o.key);
        chosenLabel = chosen.map((x) => String(x).toUpperCase()).join(", ");
        correctLabel = correct.join(", ");
      } else if (qt === "true_false") {
        chosenLabel = String(studentVal || "");
        correctLabel = String(q.trueFalseAnswer || "True");
      } else if (qt === "match") {
        chosenLabel =
          Number.isInteger(studentVal) && studentVal >= 0 && studentVal <= 3
            ? `Option ${Number(studentVal) + 1}`
            : "";
        correctLabel =
          Number.isInteger(q.correctOptionIndex) && q.correctOptionIndex >= 0
            ? `Option ${q.correctOptionIndex + 1}`
            : "";
      } else if (qt === "assertion_reason" || qt === "ar") {
        const toLetter = (v) => {
          const n = Number(v);
          if (Number.isFinite(n)) return String.fromCharCode(65 + Math.max(0, Math.floor(n)));
          return String(v || "").toUpperCase();
        };
        chosenLabel = toLetter(studentVal);
        correctLabel = toLetter(q.arAnswer || "A");
      } else {
        chosenLabel = String(studentVal ?? "");
        correctLabel = "—";
      }

      items.push({
        id: qid,
        parentId: null,
        indexLabel: "",
        subject: q.subject || "General",
        topic: q.topic || "—",
        type: tLabel,
        typeLabel: tLabel,
        questionHtml: stemHtml,
          passageHtml: q.passageHtml || "",                         // ⬅️ add this

        explanationHtml: q.explanationHtml || "",
        options: q.options || [],
        chosen: chosenLabel,
        correct: correctLabel,
        awarded: Number(evalInfo?.marks ?? 0) || 0,
        marks: getMarks(q),
        correctness,
        sectionKey: q.sectionKey || "",
        isCase: qt === "case_based",
      });
    });

    return items;
  }, [attempt, questions]);

  const breakdown = useMemo(() => {
    if (!attempt || !questions.length) {
      return { subject: [], topic: [] };
    }
    const rbq =
      attempt.resultsByQuestion && typeof attempt.resultsByQuestion === "object"
        ? attempt.resultsByQuestion
        : {};

    const rows = [];
    const qMap = new Map(questions.map((q) => [q.id, q]));
    const subToParent = new Map();
    questions.forEach((q) => {
      if (norm(q.questionType) === "case_based" && Array.isArray(q.caseSubQs)) {
        q.caseSubQs.forEach((sq, idx) => {
          subToParent.set(subIdFor(q, sq, idx), q);
        });
      }
    });

    Object.entries(rbq).forEach(([qid, evalInfo]) => {
      const parentQ = qMap.get(qid) || subToParent.get(qid);
      if (!parentQ) return;

      const isSub = subToParent.has(qid);
      const marks = (() => {
        if (!isSub) return Number(parentQ.marks ?? 1) || 1;
        const sq = (parentQ.caseSubQs || []).find((sq, idx) => subIdFor(parentQ, sq, idx) === qid);
        return Number(sq?.marks ?? parentQ.marks ?? 1) || 1;
      })();

      const subject = String(parentQ.subject || parentQ.subjectName || "General");
      const topics = Array.isArray(parentQ.topics)
        ? parentQ.topics
        : parentQ.topic
        ? [parentQ.topic]
        : ["General"];

      rows.push({
        subject,
        topics,
        marks,
        awarded: Number(evalInfo?.marks ?? 0) || 0,
        correct: !!evalInfo?.correct,
      });
    });

    // Subject aggregation
    const bySub = new Map();
    rows.forEach((r) => {
      const arr = bySub.get(r.subject) || [];
      arr.push(r);
      bySub.set(r.subject, arr);
    });
    const subject = Array.from(bySub.entries())
      .map(([sub, items]) => {
        const totalQ = items.length;
        const correctQ = items.filter((x) => x.correct).length;
        const sumMarks = items.reduce((s, x) => s + x.marks, 0);
        const sumAward = items.reduce((s, x) => s + x.awarded, 0);
        const acc = totalQ ? Math.round((correctQ / totalQ) * 100) : 0;
        return { key: sub, totalQ, correctQ, acc, sumMarks, sumAward };
      })
      .sort((a, b) => a.key.localeCompare(b.key));

    // Topic aggregation
    const topicRows = [];
    rows.forEach((r) =>
      r.topics.forEach((t) =>
        topicRows.push({
          topic: String(t),
          correct: r.correct,
          marks: r.marks,
          awarded: r.awarded,
        })
      )
    );
    const byTopic = new Map();
    topicRows.forEach((r) => {
      const arr = byTopic.get(r.topic) || [];
      arr.push(r);
      byTopic.set(r.topic, arr);
    });
    const topic = Array.from(byTopic.entries())
      .map(([t, items]) => {
        const totalQ = items.length;
        const correctQ = items.filter((x) => x.correct).length;
        const sumMarks = items.reduce((s, x) => s + x.marks, 0);
        const sumAward = items.reduce((s, x) => s + x.awarded, 0);
        const acc = totalQ ? Math.round((correctQ / totalQ) * 100) : 0;
        return { key: t, totalQ, correctQ, acc, sumMarks, sumAward };
      })
      .sort((a, b) => a.key.localeCompare(b.key));

    return { subject, topic };
  }, [attempt, questions]);

  // ------------ FILTERS & LIST ------------
  const subjectOptions = useMemo(
    () => ["all", ...Array.from(new Set(qItems.map((i) => i.subject))).sort()],
    [qItems]
  );
  const topicOptions = useMemo(() => {
    const list = Array.from(new Set(qItems.map((i) => i.topic).filter(Boolean)));
    return ["all", ...list.sort()];
  }, [qItems]);
  const typeOptions = useMemo(() => {
    const list = Array.from(new Set(qItems.map((i) => i.typeLabel)));
    return ["all", ...list.sort()];
  }, [qItems]);

  const filteredQItems = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return qItems.filter((i) => {
      if (fSubject !== "all" && i.subject !== fSubject) return false;
      if (fTopic !== "all" && i.topic !== fTopic) return false;
      if (fType !== "all" && i.typeLabel !== fType) return false;
      if (fCorrect !== "all" && i.correctness !== fCorrect) return false;

      if (needle) {
        const plain = (i.questionHtml || "").replace(/<[^>]*>/g, " ").toLowerCase();
        const plainPass = (i.passageHtml || "").replace(/<[^>]*>/g, " ").toLowerCase(); // ⬅️ add

        const hay = [i.subject, i.topic, i.typeLabel, i.correct, i.chosen, plain,plainPass]
          .map((s) => String(s || "").toLowerCase())
          .join(" ");
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [qItems, fSubject, fTopic, fType, fCorrect, q]);

  // ------------ TIME LABEL ------------
  const timeLabel = useMemo(() => {
    const s = scoreInfo.timeSec || 0;
    const d = dayjs.duration(s, "seconds");
    const hh = String(Math.floor(d.asHours())).padStart(2, "0");
    const mm = String(d.minutes()).padStart(2, "0");
    const ss = String(d.seconds()).padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
  }, [scoreInfo.timeSec]);

  // ------------ MAP + SCROLLING ------------
  const mapBySubjectSection = useMemo(() => {
    const map = new Map(); // subject -> sectionKey -> items[]
    qItems.forEach((it, idx) => {
      const sub = it.subject || "General";
      const sec = it.sectionKey || "";
      if (!map.has(sub)) map.set(sub, new Map());
      const secMap = map.get(sub);
      if (!secMap.has(sec)) secMap.set(sec, []);
      secMap.get(sec).push({ ...it, displayIndex: idx + 1 });
    });

    for (const [, secMap] of map.entries()) {
      for (const [, list] of secMap.entries()) {
        list.sort((a, b) => a.displayIndex - b.displayIndex);
      }
    }
    return map;
  }, [qItems]);

  const rowRefs = useRef({});
  useEffect(() => {
    if (pendingScrollId) {
      const row = rowRefs.current[pendingScrollId];
      if (row) {
        row.scrollIntoView({ behavior: "smooth", block: "center" });
        const detail = row.nextSibling;
        if (detail?.dataset?.detail === "1") {
          detail.style.display = "table-row";
        }
      }
      setPendingScrollId(null);
    }
  }, [filteredQItems, pendingScrollId]);

  const goToQuestion = (qid) => {
    setQ("");
    setFSubject("all");
    setFTopic("all");
    setFType("all");
    setFCorrect("all");
    setShowMap(false);
    setTimeout(() => setPendingScrollId(qid), 120);
  };

  // ------------ PERSIST ANALYSIS ------------
  const analysisSig = useMemo(() => {
    if (!attempt) return "";
    return JSON.stringify({
      s: Math.round((scoreInfo.score || 0) * 100) / 100,
      t: scoreInfo.total || 0,
      a: attempt.answeredCount || 0,
      c: attempt.correctCount || 0,
      p: Math.round(scoreInfo.percent || 0),
      r: ranking?.rank ?? null,
    });
  }, [attempt, scoreInfo, ranking]);

  const lastSigRef = useRef("");
  useEffect(() => {
    if (!schoolId || !examId || !uid) return;
    if (!exam || !attempt) return;

    const readyForSave =
      Array.isArray(questions) &&
      (questions.length > 0 || Number(attempt?.totalMarks || 0) > 0) &&
      Array.isArray(allAttempts);
    if (!readyForSave) return;

    if (analysisSig && lastSigRef.current === analysisSig) return;

    const payload = buildAnalysisPayload({
      exam,
      attempt,
      scoreInfo,
      breakdown,
      ranking,
      examTotalMarks,
    });

    saveAnalysisToFirestore({ schoolId, examId, uid, payload });
    lastSigRef.current = analysisSig || "";
  }, [
    schoolId,
    examId,
    uid,
    exam,
    attempt,
    questions,
    allAttempts,
    scoreInfo,
    breakdown,
    ranking,
    examTotalMarks,
    analysisSig,
  ]);

  // ------------ UI ------------
  if (loading) {
    return (
      <div className="container py-4 text-center">
        <Spinner animation="border" size="sm" /> Loading results…
      </div>
    );
  }

  if (err) {
    return (
      <div className="container py-4">
        <Alert variant="danger" className="mb-3">
          {err}
        </Alert>
        <Button variant="outline-secondary" onClick={() => nav(-1)}>
          Back
        </Button>
      </div>
    );
  }

  if (!exam) {
    return (
      <div className="container py-4">
        <Alert variant="warning">Exam not found.</Alert>
        <Button variant="outline-secondary" onClick={() => nav(-1)}>
          Back
        </Button>
      </div>
    );
  }

  if (!attempt) {
    return (
      <div className="container py-4">
        <Alert variant="info">No attempt found for this exam.</Alert>
        <Button variant="primary" onClick={() => nav(`/student/exams`)}>
          Go to Exams
        </Button>
      </div>
    );
  }

  const submitted = norm(attempt.status) === "submitted";

  return (
    <div className="container-fluid p-0">
      {/* Header / Summary */}
      <div
        className="rounded-3 p-3 p-md-4 mb-3"
        style={{
          background:
            "linear-gradient(135deg, rgba(32, 201, 151, .12), rgba(13, 110, 253, .08))",
          border: "1px solid rgba(0,0,0,.05)",
        }}
      >
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
          <div>
            <h4 className="m-0 fw-bold">{exam.name || "Exam Results"}</h4>
            <div className="opacity-75">
              {exam.date ? dayjs(exam.date).format("DD MMM YYYY") : "—"}{" "}
              • {exam.startTime ?? "—"}–{exam.endTime ?? "—"} •{" "}
              <Badge bg="light" text="dark">
                {exam.examType || "—"}
              </Badge>
            </div>
            <div className="opacity-75 mt-1">
              {studentName || "Student"}{" "}
              {attempt.byStudent?.rollNo ? `• Roll ${attempt.byStudent.rollNo} ` : ""}•
              {" "}
              {attempt.className || exam.classIds?.[0] || "Class"}-
              {attempt.section || "—"}
            </div>
          </div>

          {/* Overall meter */}
          <div className="text-end w-100 w-md-auto">
            <div className="fw-semibold">Overall</div>
            <div style={{ minWidth: 260 }}>
              <ProgressBar
                now={scoreInfo.percent}
                label={`${Math.round(scoreInfo.percent)}%`}
                style={{ height: 20, borderRadius: 12 }}
                variant={
                  scoreInfo.percent >= 75
                    ? "success"
                    : scoreInfo.percent >= 50
                    ? "info"
                    : scoreInfo.percent >= 33
                    ? "warning"
                    : "danger"
                }
              />
              <div className="small text-muted mt-1">
                Score: <b>{scoreInfo.score}</b> / {scoreInfo.total || "—"}
              </div>
              <div className="small text-muted">
                Time: <b>{timeLabel}</b> •{" "}
                <span className="text-muted">
                  {submitted ? "Submitted" : "Not submitted"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* KPI tiles */}
        <Row className="g-2 mt-2">
          <Col xs={6} md={2}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="py-2 text-center">
                <div className="text-muted small">Rank</div>
                <div className="fw-bold" style={{ fontSize: 20 }}>
                  {ranking.rank ? `#${ranking.rank}` : "—"}
                </div>
                <div className="text-muted small">/ {allAttempts.length}</div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} md={2}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="py-2 text-center">
                <div className="text-muted small">Percentile</div>
                <div className="fw-bold" style={{ fontSize: 20 }}>
                  {ranking.percentile != null ? ranking.percentile : "—"}
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} md={2}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="py-2 text-center">
                <div className="text-muted small">Accuracy</div>
                <div className="fw-bold" style={{ fontSize: 20 }}>
                  {Number.isFinite(scoreInfo.accuracy) ? `${Math.round(scoreInfo.accuracy)}%` : "—"}
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} md={2}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="py-2 text-center">
                <div className="text-muted small">Attempted</div>
                <div className="fw-bold" style={{ fontSize: 20 }}>
                  {attempt.answeredCount ?? "—"}
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} md={2}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="py-2 text-center">
                <div className="text-muted small">Correct</div>
                <div className="fw-bold" style={{ fontSize: 20 }}>
                  {attempt.correctCount ?? "—"}
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} md={2}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="py-2 text-center">
                <div className="text-muted small">Marks</div>
                <div className="fw-bold" style={{ fontSize: 20 }}>
                  {scoreInfo.score} / {scoreInfo.total}
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} md={2} className="d-flex align-items-stretch">
            <Button
              className="w-100"
              variant="outline-primary"
              onClick={() => setShowMap(true)}
              title="Open question map"
            >
              Question Map
            </Button>
          </Col>
        </Row>
      </div>

      {/* Filter bar */}
      <Card className="border-0 shadow-sm mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="fw-semibold">Filters</div>
            <Button
              size="sm"
              variant="outline-secondary"
              className="d-md-none"
              onClick={() => setFiltersOpen((s) => !s)}
              aria-controls="filters-collapse"
              aria-expanded={filtersOpen}
            >
              {filtersOpen ? "Hide" : "Show"}
            </Button>
          </div>

          <Collapse in={filtersOpen || true /* keep visible on desktop via grid */}>
            <div id="filters-collapse">
              <Row className="g-2 align-items-center">
                <Col xl={4} lg={5} md={6}>
                  <InputGroup>
                    <InputGroup.Text className="bg-light">
                      <i className="bi bi-search" />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search question / subject / topic / your answer"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                <Col xl={2} lg={3} md={6}>
                  <Form.Select value={fSubject} onChange={(e) => setFSubject(e.target.value)}>
                    {subjectOptions.map((s) => (
                      <option key={s} value={s}>
                        {s === "all" ? "All subjects" : s}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xl={2} lg={2} md={6}>
                  <Form.Select value={fTopic} onChange={(e) => setFTopic(e.target.value)}>
                    {topicOptions.map((t) => (
                      <option key={t} value={t}>
                        {t === "all" ? "All topics" :  t }
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xl={2} lg={2} md={6}>
                  <Form.Select value={fType} onChange={(e) => setFType(e.target.value)}>
                    {typeOptions.map((t) => (
                      <option key={t} value={t}>
                        {t === "all" ? "All types" : t}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xl={2} lg={2} md={6}>
                  <Form.Select value={fCorrect} onChange={(e) => setFCorrect(e.target.value)}>
                    <option value="all">All</option>
                    <option value="correct">Correct</option>
                    <option value="wrong">Wrong</option>
                    <option value="unattempted">Unattempted</option>
                  </Form.Select>
                </Col>
              </Row>
              <div className="mt-2 d-flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => {
                    setQ("");
                    setFSubject("all");
                    setFTopic("all");
                    setFType("all");
                    setFCorrect("all");
                  }}
                >
                  Reset Filters
                </Button>
                <div className="small text-muted d-flex align-items-center">
                  Showing <b className="ms-1 me-1">{filteredQItems.length}</b> of {qItems.length}
                </div>
              </div>
            </div>
          </Collapse>
        </Card.Body>
      </Card>

      {/* Subject-wise breakdown */}
      <Card className="border-0 shadow-sm mb-3">
        <Card.Header className="fw-semibold">Subject-wise Performance</Card.Header>
        <Card.Body className="p-0">
          {breakdown.subject.length === 0 ? (
            <div className="p-3 text-muted">No subject data available.</div>
          ) : (
            <Table striped hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>Subject</th>
                  <th className="text-end">Questions</th>
                  <th className="text-end">Correct</th>
                  <th className="text-end">Accuracy</th>
                  <th className="text-end">Marks</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.subject.map((r) => (
                  <tr key={r.key}>
                    <td>{r.key}</td>
                    <td className="text-end">{r.totalQ}</td>
                    <td className="text-end">{r.correctQ}</td>
                    <td className="text-end">{r.acc}%</td>
                    <td className="text-end">
                      {r.sumAward} / {r.sumMarks}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Topic-wise breakdown */}
      <Card className="border-0 shadow-sm mb-3">
        <Card.Header className="fw-semibold">Topic-wise Performance</Card.Header>
        <Card.Body className="p-0">
          {breakdown.topic.length === 0 ? (
            <div className="p-3 text-muted">No topic data available.</div>
          ) : (
            <div style={{ maxHeight: 320, overflowY: "auto" }}>
              <Table striped hover responsive className="mb-0">
                <thead style={{ position: "sticky", top: 0, background: "var(--bs-body-bg)" }}>
                  <tr>
                    <th>Topic</th>
                    <th className="text-end">Questions</th>
                    <th className="text-end">Correct</th>
                    <th className="text-end">Accuracy</th>
                    <th className="text-end">Marks</th>
                  </tr>
                </thead>
                <tbody>
                  {breakdown.topic.map((r) => (
                    <tr key={r.key}>
                      <td>{r.key}</td>
                      <td className="text-end">{r.totalQ}</td>
                      <td className="text-end">{r.correctQ}</td>
                      <td className="text-end">{r.acc}%</td>
                      <td className="text-end">
                        {r.sumAward} / {r.sumMarks}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Leaderboard */}
      <Card className="border-0 shadow-sm mb-3">
        <Card.Header className="fw-semibold">Leaderboard (Top 10)</Card.Header>
        <Card.Body className="p-0">
          {ranking.top.length === 0 ? (
            <div className="text-muted p-3">No attempts to show yet.</div>
          ) : (
            <ListGroup variant="flush">
              {ranking.top.map((r, i) => (
                <ListGroup.Item
                  key={r.id}
                  className="py-2 d-flex align-items-center justify-content-between"
                >
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="rounded-circle bg-light text-center"
                      style={{ width: 32, height: 32, lineHeight: "32px" }}
                    >
                      <strong>{i + 1}</strong>
                    </div>
                    <div className="fw-semibold">{r.name}</div>
                  </div>
                  <div className="text-end small">
                    <div>
                      <b>{Math.round(r.percent)}%</b>
                    </div>
                    <div className="text-muted">
                      {r.score} / {r.total || "—"}
                    </div>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Card.Body>
      </Card>

      {/* Question-wise Analysis */}
      <Card className="border-0 shadow-sm mb-5">
        <Card.Header className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
          <div className="fw-semibold">Question-wise Analysis</div>
          <div className="small text-muted">
            Tap a row to view the full question, options & explanation
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {filteredQItems.length === 0 ? (
            <div className="p-3 text-muted">Nothing to show. Try changing filters.</div>
          ) : (
            <Table hover responsive className="mb-0 align-middle">
              <thead>
                <tr>
                  <th style={{ width: 60 }} className="text-center">#</th>
                  <th>Subject / Topic</th>
                  <th>Type</th>
                  <th className="text-center d-none d-sm-table-cell">Your Answer</th>
                  <th className="text-center d-none d-sm-table-cell">Correct</th>
                  <th className="text-center">Marks</th>
                  <th className="text-center">Result</th>
                </tr>
              </thead>
              <tbody>
                {filteredQItems.map((it, idx) => (
                  <React.Fragment key={it.id}>
                    <tr
                      ref={(el) => (rowRefs.current[it.id] = el)}
                      style={{ cursor: "pointer" }}
                      onClick={(e) => {
                        const nxt = e.currentTarget.nextSibling;
                        if (nxt && nxt.dataset && nxt.dataset.detail === "1") {
                          nxt.style.display = nxt.style.display === "none" ? "table-row" : "none";
                        }
                      }}
                    >
                      <td className="text-center">
                        {it.indexLabel || idx + 1}
                      </td>
                      <td>
                        <div className="fw-semibold">{it.subject}</div>
                        <div className="text-muted small text-truncate" title={`${it.topic}${it.sectionKey ? ` • Sec ${it.sectionKey}` : ""}`}>
                          {it.topic} {it.sectionKey ? `• Sec ${it.sectionKey}` : ""}
                        </div>
                        {/* On mobile, show quick answers inline */}
                        <div className="d-sm-none mt-1 small">
                          <span className="me-2">You: {it.chosen || "—"}</span>
                          <span className="text-muted">Correct: {it.correct || "—"}</span>
                        </div>
                      </td>
                      <td>
                        <Badge bg="light" text="dark">{it.typeLabel}</Badge>
                      </td>
                      <td className="text-center d-none d-sm-table-cell">
                        {it.chosen ? (
                          <Badge bg="light" text="dark">{it.chosen}</Badge>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="text-center d-none d-sm-table-cell">
                        {it.correct ? (
                          <Badge bg="info" text="dark">{it.correct}</Badge>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="text-center">
                        <span className={it.awarded > 0 ? "text-success" : it.awarded < 0 ? "text-danger" : "text-muted"}>
                          {it.awarded > 0 ? `+${it.awarded}` : it.awarded}
                        </span>{" "}
                        <span className="text-muted">/ {it.marks}</span>
                      </td>
                      <td className="text-center">
                        {it.correctness === "correct" && <Badge bg="success">Correct</Badge>}
                        {it.correctness === "wrong" && <Badge bg="danger">Wrong</Badge>}
                        {it.correctness === "unattempted" && <Badge bg="secondary">Unattempted</Badge>}
                      </td>
                    </tr>
                    <tr data-detail="1" style={{ display: "none", background: "rgba(0,0,0,.02)" }}>
                      <td colSpan={7} className="p-3">
                        {/* Stem */}
                        <div className="mb-2">
                          <div className="text-muted small">Question</div>
                          {it.passageHtml ? (
  <div className="mb-2">
    <div className="text-muted small">Passage</div>
    <div
      dangerouslySetInnerHTML={{
        __html: it.passageHtml || "<em class='text-muted'>No passage</em>",
      }}
    />
  </div>
) : null}
                          <div dangerouslySetInnerHTML={{ __html: it.questionHtml || "<em class='text-muted'>No content</em>" }} />
                        </div>

                        {/* A/R standard choices (informational) */}
                        {it.typeLabel === "A/R" && (
                          <div className="mt-2 small text-muted">
                            <ol className="mb-0">
                              <li>A — Both statements true, Reason is the correct explanation</li>
                              <li>B — Both statements true, but Reason is not the explanation</li>
                              <li>C — Assertion true, Reason false</li>
                              <li>D — Assertion false</li>
                            </ol>
                          </div>
                        )}

                        {/* Options */}
                        {(it.options || []).length > 0 && (
                          <Row className="g-2 mb-2">
                            {it.options.map((o) => {
                              const chosenSet = new Set(
                                (it.chosen || "")
                                  .split(",")
                                  .map((s) => s.trim())
                                  .filter(Boolean)
                              );
                              const correctSet = new Set(
                                (it.correct || "")
                                  .split(",")
                                  .map((s) => s.trim())
                                  .filter(Boolean)
                              );
                              const isChosen = chosenSet.has(o.key);
                              const isCorrect = correctSet.has(o.key);
                              const variant = isCorrect ? "success" : isChosen ? "danger" : "light";
                              return (
                                <Col md={6} key={o.key}>
                                  <Card className="h-100" border={isCorrect ? "success" : isChosen ? "danger" : "light"}>
                                    <Card.Body className="py-2">
                                      <div className="d-flex align-items-start gap-2">
                                        <div style={{ width: 28 }}>
                                          <Badge bg={variant} text={variant === "light" ? "dark" : undefined}>
                                            {o.key}
                                          </Badge>
                                        </div>
                                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                          <div dangerouslySetInnerHTML={{ __html: o.value || "<em class='text-muted'>—</em>" }} />
                                        </div>
                                      </div>
                                    </Card.Body>
                                  </Card>
                                </Col>
                              );
                            })}
                          </Row>
                        )}

                        {/* Explanation */}
                        {it.explanationHtml ? (
                          <>
                            <hr />
                            <div className="text-muted small">Explanation</div>
                            <div
                              className="mt-1"
                              dangerouslySetInnerHTML={{
                                __html:
                                  it.explanationHtml ||
                                  "<em class='text-muted'>No explanation provided.</em>",
                              }}
                            />
                          </>
                        ) : (
                          <div className="text-muted small">
                            <em>No explanation provided.</em>
                          </div>
                        )}
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Footer actions */}
      <div className="d-flex justify-content-between flex-wrap gap-2 mb-4">
        <Button variant="outline-secondary" onClick={() => nav(-1)}>
          Back
        </Button>
        <Button variant="primary" onClick={() => nav(`/student/exams`)}>
          Go to Exams
        </Button>
      </div>

      {/* Question Map Modal */}
     <Modal show={showMap} onHide={() => setShowMap(false)} size="lg" centered>
  <Modal.Header closeButton>
    <Modal.Title>Question Map — Subject & Section</Modal.Title>
  </Modal.Header>
  <Modal.Body>
    {qItems.length === 0 ? (
      <div className="text-muted">No questions.</div>
    ) : (
      Array.from(mapBySubjectSection.entries()).map(([subject, secMap]) => (
        <div key={subject} className="mb-3">
          <div className="fw-semibold mb-2">{subject}</div>

          {/* ✅ Sort sections alphabetically before rendering */}
          {Array.from(secMap.entries())
            .sort(([secA], [secB]) => {
              if (!secA) return 1; // "General" or empty goes last
              if (!secB) return -1;
              return secA.localeCompare(secB);
            })
            .map(([sec, list]) => (
              <div key={subject + "-" + sec} className="mb-2">
                <div className="text-muted small mb-1">
                  Section: {sec || "General"}
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {list.map((it) => {
                    const isBlank = !it.chosen;
                    const isCorrect = it.correctness === "correct";
                    const bg = isBlank
                      ? "secondary"
                      : isCorrect
                      ? "success"
                      : "danger";
                    const title = isBlank
                      ? "Unattempted"
                      : isCorrect
                      ? "Correct"
                      : "Wrong";
                    return (
                      <Button
                        key={it.id}
                        size="sm"
                        variant={bg}
                        onClick={() => goToQuestion(it.id)}
                        title={`${title} — Q#${it.indexLabel || it.displayIndex}`}
                      >
                        {it.indexLabel || it.displayIndex}
                      </Button>
                    );
                  })}
                </div>
              </div>
            ))}
        </div>
      ))
    )}
    <div className="small text-muted mt-3 d-flex gap-2 align-items-center">
      <Badge bg="success">Correct</Badge>
      <Badge bg="danger">Wrong</Badge>
      <Badge bg="secondary">Unattempted</Badge>
    </div>
  </Modal.Body>
  <Modal.Footer>
    <Button variant="secondary" onClick={() => setShowMap(false)}>
      Close
    </Button>
  </Modal.Footer>
</Modal>

    </div>
  );
}
